import {
  Button,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import { Column } from "../Column";
import { LimitEntriesCard } from "./LimitEntriesCard";
import { ShowDetailsCard } from "./ShowDetailsCard";
import { OrderingCard } from "./OrderingCard";
import { useTranslation } from "react-i18next";

interface Props<E> {
  columns: Column<E>[];
  changeOrderBy: (newField: keyof E) => void;
  currentOrder: "asc" | "desc";
  currentField: keyof E;
  showDetails: boolean;
  setShowDetails: (value: ((prevState: boolean) => boolean) | boolean) => void;
  entryLimit: number;
  setEntryLimit: (value: ((prevState: number) => number) | number) => void;
  isModalOpen: boolean;
  setIsModalOpen: (value: ((prevState: boolean) => boolean) | boolean) => void;
  limitOptions: number[];
}

export function SettingsModal<E>({
  columns,
  changeOrderBy,
  currentOrder,
  currentField,
  showDetails,
  setShowDetails,
  entryLimit,
  setEntryLimit,
  isModalOpen,
  setIsModalOpen,
  limitOptions,
}: Props<E>) {
  const toggle = () => setIsModalOpen((v) => !v);

  const handleChangeOrderBy = (newField: keyof E) => {
    changeOrderBy(newField);
    setIsModalOpen(false);
  };

  const handleSetShowDetails = (
    value: ((prevState: boolean) => boolean) | boolean,
  ) => {
    setShowDetails(value);
    setIsModalOpen(false);
  };

  const handleSetEntryLimit = (
    value: number | ((prevState: number) => number),
  ) => {
    setEntryLimit(value);
    setIsModalOpen(false);
  };

  const { t } = useTranslation();

  return (
    <Modal size="xl" isOpen={isModalOpen} fullscreen="md" toggle={toggle}>
      <ModalHeader toggle={toggle}>{t("common.settings")}</ModalHeader>
      <ModalBody>
        <Row className="align-items-stretch g-2">
          <Col md={12} lg={4}>
            <OrderingCard<E>
              columns={columns}
              changeOrderBy={handleChangeOrderBy}
              currentOrder={currentOrder}
              currentField={currentField}
            />
          </Col>
          <Col md={12} lg={4}>
            <ShowDetailsCard
              showDetails={showDetails}
              setShowDetails={handleSetShowDetails}
            />
          </Col>
          <Col md={12} lg={4}>
            <LimitEntriesCard
              title={t("common.numberOfEntries")}
              entryLimit={entryLimit}
              setEntryLimit={handleSetEntryLimit}
              limitOptions={limitOptions}
            />
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={toggle}>
          {t("common.buttons.close")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
